import { useState, useEffect, useCallback } from 'react';

export const useVoiceSynthesis = () => {
  const [voices, setVoices] = useState([]);
  const [voiceMap, setVoiceMap] = useState({});

  // Initialize voices
  useEffect(() => {
    const loadVoices = () => {
      const availableVoices = window.speechSynthesis.getVoices();
      setVoices(availableVoices);
      
      // ONLY use local voices for speed and reliability
      // Cloud-based voices can have significant network delays
      const localVoices = availableVoices.filter(v => v.localService === true);
      
      // Prioritize American English local voices
      const englishVoices = localVoices.filter(v => 
        v.lang.startsWith('en-') || v.lang === 'en'
      );
      
      const americanVoices = englishVoices.filter(v => 
        v.lang === 'en-US' || v.lang.startsWith('en-US')
      );
      
      // Use voices in priority order: American English > English > Local > All
      const voicesToUse = americanVoices.length > 0 ? americanVoices 
                        : englishVoices.length > 0 ? englishVoices 
                        : localVoices.length > 0 ? localVoices 
                        : availableVoices; // Absolute fallback
      
      // Common reliable local voice names across platforms
      // macOS: Alex, Samantha, Victoria, Fred, Karen
      // Windows: David, Zira, Mark, Hazel
      // Linux: espeak voices
      const reliableVoiceNames = /^(alex|samantha|victoria|karen|susan|fred|david|zira|mark|hazel|daniel|fiona)/i;
      
      const reliableVoices = voicesToUse.filter(v => reliableVoiceNames.test(v.name));
      const voicePool = reliableVoices.length >= 2 ? reliableVoices : voicesToUse;
      
      // Pick 2 most different voices (avoid duplicates)
      const pickTwo = (voices) => {
        if (voices.length === 0) return [null, null];
        if (voices.length === 1) return [voices[0], voices[0]];
        
        // Try to pick voices with different base names
        const selected = [];
        const usedNames = new Set();
        
        for (const voice of voices) {
          if (selected.length >= 2) break;
          const baseName = voice.name.split(/[(\s]/)[0];
          if (!usedNames.has(baseName)) {
            selected.push(voice);
            usedNames.add(baseName);
          }
        }
        
        // Fill remaining slots
        while (selected.length < 2 && voices.length > selected.length) {
          selected.push(voices[selected.length]);
        }
        
        return selected;
      };
      
      const [voice1, voice2] = pickTwo(voicePool);
      
      // Use just 2 voices instead of 4 for simplicity and consistency
      setVoiceMap({
        male1: voice1 || voicePool[0] || availableVoices[0],
        male2: voice2 || voicePool[1] || availableVoices[1],
        female1: voice2 || voicePool[1] || availableVoices[1], // Reuse voice2
        female2: voice1 || voicePool[0] || availableVoices[0]  // Reuse voice1
      });
      
      console.log('Selected voices:', {
        voice1: voice1?.name,
        voice2: voice2?.name,
        local: voice1?.localService,
        totalAvailable: availableVoices.length,
        localCount: localVoices.length
      });
      
      // Pre-warm the speech synthesis to prevent first-use delay
      const warmUp = new SpeechSynthesisUtterance('');
      warmUp.volume = 0;
      window.speechSynthesis.speak(warmUp);
    };

    loadVoices();
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
    
    // Additional warm-up after short delay
    setTimeout(() => {
      const warmUp = new SpeechSynthesisUtterance('');
      warmUp.volume = 0;
      window.speechSynthesis.speak(warmUp);
    }, 100);
  }, []);

  // Play audio with voice
  const playAudio = useCallback((letter, voiceType, isVoiceOnly = false, volumeMultiplier = 1.0) => {
    // Cancel any ongoing speech immediately and aggressively
    window.speechSynthesis.cancel();
    
    // Use requestAnimationFrame for better timing synchronization
    requestAnimationFrame(() => {
      // If only voice dimension (not audio), say "this voice" for more differentiation
      // For letters, use lowercase to prevent TTS from saying "Capital H" or "Capitol H"
      let textToSpeak;
      if (isVoiceOnly) {
        textToSpeak = 'this voice';
      } else if (typeof letter === 'string' && letter.length === 1 && /[A-Z]/.test(letter)) {
        // Single capital letter - convert to lowercase to avoid phonetic spelling
        textToSpeak = letter.toLowerCase();
      } else {
        textToSpeak = letter;
      }
      const utterance = new SpeechSynthesisUtterance(textToSpeak);
      
      // Check if voiceType is a language code (like 'zh-CN') instead of a mapped voice
      if (voiceType === 'zh-CN' || voiceType.startsWith('zh-')) {
        // Find a Chinese voice
        const availableVoices = window.speechSynthesis.getVoices();
        const chineseVoice = availableVoices.find(v => 
          v.lang === 'zh-CN' || v.lang.startsWith('zh-')
        );
        
        if (chineseVoice) {
          utterance.voice = chineseVoice;
          utterance.lang = chineseVoice.lang;
        } else {
          // Fallback: just set the language and let browser choose
          utterance.lang = 'zh-CN';
        }
        utterance.rate = 1.2; // Faster rate for consistency with English letters
      } else {
        // Use mapped voice (normal English voices)
        const voice = voiceMap[voiceType];
        if (voice) {
          utterance.voice = voice;
          utterance.lang = voice.lang; // Explicitly set language
        }
        // Increase rate for snappier, more consistent timing across voices
        utterance.rate = 1.5; // Faster speech = more consistent duration
      }
      
      // Optimize for clear pronunciation and apply volume multiplier
      utterance.pitch = 1.0;
      // NOTE: SpeechSynthesis API caps volume at 1.0 (cannot amplify beyond 100%)
      // For volume dimension with values > 1.0, use normalized MP3 files instead
      utterance.volume = Math.min(1.0, 1.0 * volumeMultiplier); // Speech API limitation
      
      // Add safety timeout to prevent hanging voices
      let hasStarted = false;
      let hasFinished = false;
      let timeoutId = null;
      
      utterance.onstart = () => {
        hasStarted = true;
        if (timeoutId) clearTimeout(timeoutId);
        
        // Set a new timeout once started - some voices need more time
        timeoutId = setTimeout(() => {
          if (!hasFinished && window.speechSynthesis.speaking) {
            window.speechSynthesis.cancel();
          }
        }, 1500); // Give 1.5s after starting to finish
      };
      
      utterance.onend = () => {
        hasFinished = true;
        if (timeoutId) clearTimeout(timeoutId);
      };
      
      utterance.onerror = (event) => {
        // Only log non-interrupted errors (interrupted is expected when we cancel)
        if (event.error !== 'interrupted') {
          console.warn('Speech synthesis error:', event.error);
        }
        hasFinished = true;
        if (timeoutId) clearTimeout(timeoutId);
      };
      
      // Initial timeout: cancel if speech doesn't start within 200ms
      timeoutId = setTimeout(() => {
        if (!hasStarted) {
          window.speechSynthesis.cancel();
        }
      }, 200);
      
      // Play immediately
      window.speechSynthesis.speak(utterance);
    });
  }, [voiceMap]);

  return { voices, voiceMap, playAudio };
};

